import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useIntl } from "react-intl";
import { api } from "./api.js";
import styles from "./video.module.css";

/**
 * Video-iFrame, der bei Layoutwechseln NICHT neu geladen wird (Kiosk: Personal ↔ Patientenmodus,
 * Leitstand: Board ↔ Fallansicht; ARCHITEKTUR §10.5).
 * Der Host hängt genau einen iFrame ein und legt ihn per Layout über den aktuell sichtbaren
 * Platzhalter (`VideoSlot`). Ohne Platzhalter schrumpft er zu einem kleinen Bild in der Ecke –
 * das Gespräch läuft weiter und bleibt sichtbar.
 */

interface VideoCtx {
  caseId: string | null;
  setCaseId: (id: string | null) => void;
  slot: HTMLElement | null;
  setSlot: (el: HTMLElement | null) => void;
  onJoined?: () => void;
}

const Ctx = createContext<VideoCtx | null>(null);

export function useVideo() {
  const c = useContext(Ctx);
  if (!c) throw new Error("VideoProvider fehlt");
  return c;
}

export function VideoProvider({
  children,
  onJoined,
}: {
  children: ReactNode;
  onJoined?: (caseId: string) => void;
}) {
  const [caseId, setCaseId] = useState<string | null>(null);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const joined = useCallback(() => {
    if (caseId) onJoined?.(caseId);
  }, [caseId, onJoined]);
  return (
    <Ctx.Provider value={{ caseId, setCaseId, slot, setSlot, onJoined: joined }}>
      {children}
      <VideoHost />
    </Ctx.Provider>
  );
}

/** Platzhalter im Layout, über den der Host den iFrame legt */
export function VideoSlot({ caseId, className }: { caseId: string; className?: string }) {
  const v = useVideo();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    v.setCaseId(caseId);
  }, [caseId, v]);
  useLayoutEffect(() => {
    v.setSlot(ref.current);
    return () => v.setSlot(null);
  }, [v]);
  return <div ref={ref} className={className ?? styles.videoSlot} />;
}

function VideoHost() {
  const intl = useIntl();
  const { caseId, slot, onJoined } = useVideo();
  const [measured, setMeasured] = useState<{ slot: HTMLElement; rect: DOMRect } | null>(null);
  const rect = slot && measured?.slot === slot ? measured.rect : null;
  const url = useQuery({
    queryKey: ["video-url", caseId],
    queryFn: () =>
      api<{ url: string; provider: string; devOnly: boolean }>(`/cases/${caseId}/video`, {
        background: true,
      }),
    enabled: !!caseId,
    // Die URL darf sich während des Gesprächs nicht ändern (sonst lädt der iFrame neu)
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 2,
  });

  // Position je Frame prüfen: Der Platzhalter kann sich ohne Größenänderung verschieben
  // (Hinweisbänder, umbrechende Zeilen). Neu gerendert wird nur bei tatsächlicher Änderung.
  useEffect(() => {
    if (!slot) return;
    let raf = 0;
    let last = "";
    const tick = () => {
      const r = slot.getBoundingClientRect();
      const key = `${r.top}|${r.left}|${r.width}|${r.height}|${window.innerWidth}`;
      if (key !== last) {
        last = key;
        setMeasured({ slot, rect: r });
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [slot]);

  // Letzte URL festhalten: Auch wenn der Cache (z. B. bei der Sperre) geleert wird, bleibt der iFrame stehen
  const [kept, setKept] = useState<{ caseId: string; url: string; devOnly: boolean } | null>(null);
  if (caseId && url.data && (kept?.caseId !== caseId || kept.url !== url.data.url))
    setKept({ caseId, url: url.data.url, devOnly: url.data.devOnly });
  const current = kept && kept.caseId === caseId ? kept : null;
  if (!caseId || !current) return null;
  const style = rect
    ? {
        insetBlockStart: `${rect.top}px`,
        insetInlineStart: `${document.dir === "rtl" ? window.innerWidth - rect.right : rect.left}px`,
        inlineSize: `${rect.width}px`,
        blockSize: `${rect.height}px`,
      }
    : undefined;
  return (
    <div className={rect ? styles.videoHost : styles.videoPip} style={style}>
      <iframe
        src={current.url}
        title={intl.formatMessage({ id: "console.video.frameTitle" })}
        allow="camera; microphone; fullscreen; autoplay"
        className={styles.videoFrame}
        onLoad={onJoined}
      />
      {current.devOnly ? (
        <span className={styles.devVideo}>{intl.formatMessage({ id: "console.video.devOnly" })}</span>
      ) : null}
    </div>
  );
}
